import { useSelector } from "react-redux";
import { Avatar, AvatarGroup, Grid, Tooltip, Badge } from "@mui/material";
import { formatDateAndTime } from "../../../helpers/date-helper";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { selectDriver } from "../../../ducks/data/drivers/selectors";
import {
  selectRouteViewModel,
  selectMostRecentDriverLogin,
} from "../../../ducks/data/route-views/selectors";
import { selectRouteLogin } from "../../../ducks/data/driver-activities/selectors";
import { selectVehicles } from "../../../ducks/data/trailers/selectors";
import { Driver } from "../../../ducks/data/drivers/types";
import { useFeature } from "../../../hooks";
import { DateTime } from "luxon";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  // "@keyframes ripple": {
  //     "0%": {
  //         transform: "scale(.8)",
  //         opacity: 1,
  //     },
  //     "100%": {
  //         transform: "scale(2.4)",
  //         opacity: 0,
  //     },
  // },
}));

const getDriverInitials = (driver: Driver) =>
  driver.firstName[0] + driver.lastName[0];

const DriverAvatar = ({ driver }: { driver: Driver }) => (
  <Avatar
    alt={`${driver.firstName} ${driver.lastName}`}
    sx={{
      width: 24,
      height: 24,
      fontSize: 14,
      // marginTop: 0.5, //1
    }}
  >
    {getDriverInitials(driver)}
  </Avatar>
);

const DriverAvatarWithLoginTooltip = ({
  driverId,
  driverLoginActivity,
}: {
  driverId: string;
  driverLoginActivity: {
    loggedInAt: DateTime;
    driverId: string;
    truckId: string;
    trailerId: string | null;
  } | null;
}) => {
  const driver = useSelector(selectDriver(driverId));
  const trailers = useSelector(selectVehicles);
  const { t } = useTranslation("dispatching");
  let truckForDriver = null;
  let trailerForDriver = null;
  if (driver && driverLoginActivity) {
    truckForDriver = trailers.find(
      (t) => t.id === driverLoginActivity!.truckId
    );
    if (driverLoginActivity!.trailerId) {
      trailerForDriver = trailers.find(
        (t) => t.id === driverLoginActivity!.trailerId
      );
    }
  }

  return (
    driver &&
    (driverLoginActivity?.driverId === driverId ? (
      <Tooltip
        key={driverId}
        title={
          <>
            {t("routeCard.driver")}: {`${driver.firstName} ${driver.lastName}`}
            <br />
            {t("routeCard.loggedIn")}:{" "}
            {formatDateAndTime(driverLoginActivity.loggedInAt)}
            {truckForDriver && (
              <>
                <br />
                {t("compactView.columnTruck")}: {truckForDriver.number}
              </>
            )}
            {trailerForDriver && (
              <>
                <br />
                {t("compactView.columnTrailer")}: {trailerForDriver.number}
              </>
            )}
          </>
        }
      >
        <StyledBadge
          overlap="circular"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          variant="dot"
        >
          <DriverAvatar driver={driver} />
        </StyledBadge>
      </Tooltip>
    ) : (
      <DriverAvatar driver={driver} />
    ))
  );
};

export const RouteDriverWithLoginActivity = ({
  driverLoginActivity,
  drivers,
}: {
  driverLoginActivity: {
    loggedInAt: DateTime;
    driverId: string;
    truckId: string;
    trailerId: string | null;
  } | null;
  drivers: string[];
}) => {
  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid>
        <AvatarGroup
          total={drivers.length}
          slotProps={{
            additionalAvatar: {
              sx: {
                // backgroundColor: "green",
                width: 24,
                height: 24,
                fontSize: 14,
                // marginTop: 0.5, //1
              },
            },
          }}
        >
          {drivers.map((driverId) => (
            <DriverAvatarWithLoginTooltip
              key={driverId}
              driverId={driverId}
              driverLoginActivity={driverLoginActivity}
            />
          ))}
        </AvatarGroup>
        <div
          style={{
            fontSize: 14,
            lineHeight: "14px",
            marginTop: 2,
          }}
        >
          {drivers.map((d) => (
            <DriverName key={d} driverId={d} />
          ))}
        </div>
      </Grid>
    </Grid>
  );
};

const DriverName = ({ driverId }: { driverId: string }) => {
  const driver = useSelector(selectDriver(driverId));
  return (
    <div className="text-end">
      {driver ? `${driver.firstName} ${driver.lastName.charAt(0)}.` : ""}
    </div>
  );
};

const RouteDrivers = ({ routeId }: { routeId: string }) => {
  const drivers = useSelector(selectRouteViewModel(routeId))?.drivers || [];
  // route_views.driverLogins has no historic backfill, so both sources are
  // read unconditionally (hooks can't be called conditionally) and the flag
  // just picks which one to display — flip per-org once comfortable that
  // enough routes have regenerated with the new column populated.
  const driverLoginFromRouteView = useFeature("driver-login-from-route-view");
  const loginActivityFromRouteView = useSelector(
    selectMostRecentDriverLogin(routeId)
  );
  const loginActivityFromActivities = useSelector(selectRouteLogin(routeId));
  const loginActivity = driverLoginFromRouteView
    ? loginActivityFromRouteView
    : loginActivityFromActivities;
  return (
    <RouteDriverWithLoginActivity
      driverLoginActivity={loginActivity}
      drivers={drivers}
    />
  );
};

export default RouteDrivers;
